import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations'
import i18nConfig from 'utils/i18nConfig'
import Terms from 'features/terms/Terms'
import Layout from 'components/layout/Layout'
import { DEFAULT_NAMESPACES } from 'constants/i18n'
import { OLD_TERMS_VERSIONS, SORTED_OLD_TERMS_KEYS } from 'features/terms/Terms.constants'
import { Language } from 'types/language'
import { routes } from 'utils/routes'
import classNames from 'classnames'
import styles from 'features/terms/Terms.module.scss'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next/pages'
import { Heading, Header } from '@boltenergy-be/design-system'
import dayjs from 'dayjs'
import { OldTermsProps } from 'features/terms/types'
import { SeoProps } from 'components/seo/types'
import { DATE_FORMAT } from 'features/registration/constants'

const OldTermsPage = ({ version, localizations }: OldTermsProps) => {
  // Router
  const { locale } = useRouter()

  // i18n
  const { t } = useTranslation(['common', 'terms'])

  // Constants
  const seo: SeoProps = {
    title: t('terms:seo.title', { context: 'old', version: dayjs(version).format(DATE_FORMAT) }),
    description: t('terms:seo.description')
  }

  return (
    <Layout localized={localizations} {...{ seo }}>
      <Header title={t('terms.oldVersions.title')} color="light" docked shape="s4" />

      <Terms content={OLD_TERMS_VERSIONS[version]} />

      <section className={classNames(styles.terms, styles['old-versions'])}>
        <Heading as="h2" variant="h3">
          {t('terms.otherVersions.title', 'Andere Algemene Voorwaarden')}
        </Heading>
        <ul>
          <li>
            <Link href={routes(locale).terms}>
              {t('terms.currentVersion.version', 'Geldig vanaf {{ version }}', {
                version: dayjs(SORTED_OLD_TERMS_KEYS[0]).format('DD/MM/YYYY')
              })}
            </Link>
          </li>

          {SORTED_OLD_TERMS_KEYS.filter((v) => v !== version).map((version) => (
            <li key={version}>
              <Link href={routes(locale, version).oldTerms}>
                {t('terms.oldVersions.version', 'Geldig tot {{ version }}', { version: dayjs(version).format('DD/MM/YYYY') })}
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </Layout>
  )
}

export const getStaticProps = async ({ params, locale }) => {
  const { version } = params

  if (!OLD_TERMS_VERSIONS[version]) {
    return { notFound: true }
  }

  return {
    props: {
      localizations: {
        [Language.DUTCH]: routes(Language.DUTCH, version).oldTerms,
        [Language.FRENCH]: routes(Language.FRENCH, version).oldTerms
      },
      version,
      ...(await serverSideTranslations(locale, [...DEFAULT_NAMESPACES, 'terms'], i18nConfig))
    }
  }
}

export const getStaticPaths = async () => {
  const paths = Object.keys(OLD_TERMS_VERSIONS).map((version) => ({
    params: { version }
  }))

  return {
    fallback: 'blocking',
    paths
  }
}

export default OldTermsPage
